import React, { useEffect, useState } from 'react';
import { MapPin, Mail, Clock, Send } from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import '../styles/Contact.css';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple frontend validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const endpoint = apiBase
        ? `${apiBase}/api/contact/submit`   // dev: FastAPI (Emergent preview)
        : '/api/contact.php';                // prod: PHP on Hostinger
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body?.detail || 'Submission failed. Please try again.');
      }
      toast.success("Message sent successfully! We'll get back to you within 24h.");
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin size={24} />,
      title: 'Visit Us',
      details: [
        'Rua da Levada 123',
        'Funchal, 9000-123',
        'Madeira Island, Portugal'
      ]
    },
    {
      icon: <WhatsAppIcon size={24} />,
      title: 'WhatsApp Us',
      details: [
        '+351 968 656 613',
        'WhatsApp only — fastest reply',
        'Available 9 AM – 7 PM'
      ]
    },
    {
      icon: <Mail size={24} />,
      title: 'Email Us',
      details: [
        'bookings@madeiraoutdoors.com',
        'bookings@madeiraoutdoors.com',
        'Response within 24 hours'
      ]
    },
    {
      icon: <Clock size={24} />,
      title: 'Working Hours',
      details: [
        'Monday - Friday: 9 AM - 7 PM',
        'Saturday: 10 AM - 6 PM',
        'Sunday: 10 AM - 4 PM'
      ]
    }
  ];

  return (
    <div className="contact-page">
      <Toaster position="top-center" richColors />
      
      {/* Page Header */}
      <section className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <div className={`page-hero-content ${isVisible ? 'animated fadeIn' : 'animated'}`}>
            <h1 className="display-large">Contact Us</h1>
            <p className="body-large">Get in touch to start planning your perfect Madeira adventure</p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-container">
              <h2 className="heading-2">Send Us a Message</h2>
              <p className="body-medium form-description">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+351 XXX XXX XXX"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What is this regarding?"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your plans, questions, or special requests..."
                    rows="6"
                    required
                  ></textarea>
                </div>
                <button type="submit" className="btn-primary btn-submit" disabled={isSubmitting} data-testid="contact-submit-btn">
                  <Send size={20} />
                  {isSubmitting ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="contact-info-container">
              <h2 className="heading-2">Get In Touch</h2>
              <p className="body-medium info-description">
                We're here to help you plan the perfect outdoor adventure in Madeira.
              </p>
              <div className="contact-info-cards">
                {contactInfo.map((info, index) => (
                  <div key={index} className="info-card">
                    <div className="info-icon">{info.icon}</div>
                    <div className="info-content">
                      <h3 className="info-title">{info.title}</h3>
                      {info.details.map((detail, i) => (
                        <p key={i} className="info-detail">{detail}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="heading-1">Find Us</h2>
          <p className="body-large map-description">
            Visit our office in the heart of Funchal to discuss your adventure plans in person.
          </p>
          <div className="map-container">
            <iframe
              title="Madeira Outdoors Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d105869.56293182413!2d-17.012818849999998!3d32.6669159!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xc605f0ba04c5319%3A0x400d9b8f52b6910!2sFunchal%2C%20Portugal!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '16px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;